/**
 * Tax Breakdown Table Component
 * 
 * Displays and allows editing of vendor tax calculations for bid submissions.
 * Shows tax breakdown by specification/quantity/destination combination.
 */

import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatCurrency, parseCurrency } from '@/lib/types/job';
import { TaxCalculation } from '@/hooks/useVendorBidTax';
import { RotateCcw, Calculator, MapPin, Pencil, AlertCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface TaxBreakdownTableProps {
  calculations: TaxCalculation[];
  specifications: { id: string; name: string }[];
  onTaxEdit: (lineItemKey: string, newAmountCents: number) => void;
  onResetTax: (lineItemKey: string) => void;
  onResetAllTax: () => void;
  onSetAllToZero: () => void;
  totalVendorTax: number;
  totalCalculatedTax: number;
  hasEdits: boolean;
  isLoading?: boolean;
}

export const TaxBreakdownTable = ({
  calculations,
  specifications,
  onTaxEdit,
  onResetTax,
  onResetAllTax,
  onSetAllToZero,
  totalVendorTax,
  totalCalculatedTax,
  hasEdits,
  isLoading = false,
}: TaxBreakdownTableProps) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  const getSpecName = (specId: string) => {
    return specifications.find(s => s.id === specId)?.name || 'Unknown Specification';
  };

  const handleStartEdit = (calc: TaxCalculation) => {
    setEditingKey(calc.lineItemKey);
    setEditValue((calc.vendorTaxCents / 100).toFixed(2));
  };

  const handleSaveEdit = (lineItemKey: string) => {
    const newAmountCents = parseCurrency(editValue);
    onTaxEdit(lineItemKey, newAmountCents);
    setEditingKey(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent, lineItemKey: string) => {
    if (e.key === 'Enter') {
      handleSaveEdit(lineItemKey);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Group calculations by specification
  const calcsBySpec = calculations.reduce((acc, calc) => {
    if (!acc[calc.specificationId]) {
      acc[calc.specificationId] = [];
    }
    acc[calc.specificationId].push(calc);
    return acc;
  }, {} as Record<string, TaxCalculation[]>);

  if (calculations.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No tax calculations available. Click "Calculate Tax" to estimate sales tax.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Tax Breakdown
            </CardTitle>
            <CardDescription>
              Review and adjust estimated sales tax per delivery location
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onSetAllToZero}
                    disabled={isLoading}
                  >
                    Set All to $0
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Use if you don't have economic nexus in delivery states</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {hasEdits && (
              <Button
                variant="outline"
                size="sm"
                onClick={onResetAllTax}
                className="gap-1"
              >
                <RotateCcw className="h-4 w-4" />
                Reset All
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-sm text-muted-foreground space-y-2">
          <p>
            As the seller of record, you are responsible for collecting and remitting sales tax to the appropriate tax authorities. The estimates provided are informational only and do not constitute tax advice.
          </p>
          <p>
            <strong>No economic nexus?</strong> If you are not required to collect sales tax in a delivery state, you may set the tax to $0.00 for those destinations. Use "Set All to $0" if you have no nexus in any delivery states.
          </p>
        </div>
        {Object.entries(calcsBySpec).map(([specId, calcs]) => (
          <div key={specId} className="space-y-2">
            <h4 className="font-medium text-sm">{getSpecName(specId)}</h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Qty</TableHead>
                  <TableHead className="w-48">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      Destination
                    </div>
                  </TableHead>
                  <TableHead className="w-28">Taxable</TableHead>
                  <TableHead className="w-20">Rate</TableHead>
                  <TableHead className="w-28">Calculated</TableHead>
                  <TableHead className="w-36">Your Tax *</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calcs.map((calc) => (
                  <TableRow key={calc.lineItemKey}>
                    <TableCell className="font-medium">
                      {calc.quantity.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{calc.destinationName}</div>
                        <div className="text-xs text-muted-foreground">
                          {calc.destinationZip}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {formatCurrency(calc.taxableAmountCents)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {calc.taxRatePercentage.toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatCurrency(calc.calculatedTaxCents)}
                    </TableCell>
                    <TableCell>
                      {editingKey === calc.lineItemKey ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, calc.lineItemKey)}
                            onBlur={() => handleSaveEdit(calc.lineItemKey)}
                            autoFocus
                            className="w-24 h-8"
                            placeholder="$0.00"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className={calc.isEdited ? 'font-medium text-amber-600' : ''}>
                            {formatCurrency(calc.vendorTaxCents)}
                          </span>
                          {calc.isEdited && (
                            <Badge variant="outline" className="text-xs">
                              Edited
                            </Badge>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {editingKey !== calc.lineItemKey && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleStartEdit(calc)}
                                  disabled={isLoading}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit tax amount</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {calc.isEdited && editingKey !== calc.lineItemKey && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => onResetTax(calc.lineItemKey)}
                                  disabled={isLoading}
                                >
                                  <RotateCcw className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Reset to calculated</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}

        {/* Tax Summary */}
        <div className="border-t pt-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Calculated Total Tax:</span>
            <span>{formatCurrency(totalCalculatedTax)}</span>
          </div>
          <div className="flex justify-between font-semibold">
            <span>Your Submitted Tax:</span>
            <span className={hasEdits ? 'text-amber-600' : ''}>
              {formatCurrency(totalVendorTax)}
            </span>
          </div>
          {hasEdits && (
            <p className="text-xs text-muted-foreground">
              * You have modified some tax amounts from the calculated values
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};